import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTachometerAlt,
  faNewspaper,
  faEnvelope,
  faUsers,
  faChartBar,
  faCog,
  faSignOutAlt,
  faBars,
  faTimes,
  faBell,
  faUser,
  faSearch,
  faEdit
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';

const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // 侧边栏菜单项
  const menuItems = [
    {
      path: '/admin/dashboard',
      icon: faTachometerAlt,
      label: '仪表板',
      description: '系统概览'
    },
    {
      path: '/admin/content',
      icon: faEdit,
      label: '内容管理',
      description: '网站内容编辑'
    },
    {
      path: '/admin/news',
      icon: faNewspaper,
      label: '新闻管理',
      description: '文章发布与编辑'
    },
    {
      path: '/admin/contacts',
      icon: faEnvelope,
      label: '联系管理',
      description: '客户咨询处理'
    },
    {
      path: '/admin/users',
      icon: faUsers,
      label: '用户管理',
      description: '系统用户管理'
    },
    {
      path: '/admin/analytics',
      icon: faChartBar,
      label: '数据分析',
      description: '统计报表'
    },
    {
      path: '/admin/settings',
      icon: faCog,
      label: '系统设置',
      description: '配置管理'
    }
  ];

  // 获取通知
  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      // 这里应该调用API获取通知
      // const response = await api.get('/admin/notifications');
      // setNotifications(response.data);
      
      // 模拟通知数据
      setNotifications([
        {
          id: 1,
          type: 'contact',
          title: '新的客户咨询',
          message: '来自张三的产品咨询',
          time: '5分钟前',
          unread: true
        },
        {
          id: 2,
          type: 'news',
          title: '文章待审核',
          message: '有2篇文章等待审核',
          time: '1小时前',
          unread: true
        },
        {
          id: 3,
          type: 'system',
          title: '系统更新',
          message: '系统将在今晚进行维护',
          time: '2小时前',
          unread: false
        }
      ]);
    } catch (error) {
      console.error('获取通知失败:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/admin/login');
    } catch (error) {
      console.error('退出登录失败:', error);
    }
  };

  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
  };

  const markNotificationAsRead = (id) => {
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === id ? { ...notif, unread: false } : notif
      )
    );
  };

  const unreadCount = notifications.filter(n => n.unread).length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 侧边栏 */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0 lg:static lg:inset-0`}>
        {/* Logo */}
        <div className="flex items-center justify-center h-16 px-4 bg-blue-600">
          <Link to="/admin/dashboard" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
              <span className="text-blue-600 font-bold text-lg">云</span>
            </div>
            <span className="text-white font-bold text-xl">云账管理</span>
          </Link>
        </div>

        {/* 用户信息 */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <FontAwesomeIcon icon={faUser} className="text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">{user?.name || '管理员'}</p>
              <p className="text-xs text-gray-500">{user?.role === 'admin' ? '超级管理员' : '编辑'}</p>
            </div>
          </div>
        </div>

        {/* 导航菜单 */}
        <nav className="mt-4 px-2">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path || 
                           (item.path !== '/admin/dashboard' && location.pathname.startsWith(item.path));
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`group flex items-center px-3 py-3 mb-1 text-sm font-medium rounded-lg transition-colors duration-200 ${
                  isActive
                    ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
                onClick={() => setSidebarOpen(false)}
              >
                <FontAwesomeIcon 
                  icon={item.icon} 
                  className={`mr-3 h-5 w-5 ${
                    isActive ? 'text-blue-700' : 'text-gray-400 group-hover:text-gray-500'
                  }`} 
                />
                <div>
                  <div>{item.label}</div>
                  <div className="text-xs text-gray-400">{item.description}</div>
                </div>
              </Link>
            );
          })}
        </nav>

        {/* 退出登录 */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200">
          <button
            onClick={handleLogout}
            className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-600 rounded-lg hover:bg-gray-50 hover:text-gray-900 transition-colors duration-200"
          >
            <FontAwesomeIcon icon={faSignOutAlt} className="mr-3 h-5 w-5 text-gray-400" />
            退出登录
          </button>
        </div>
      </div>

      {/* 主内容区域 */}
      <div className="lg:pl-64">
        {/* 顶部导航栏 */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between px-4 py-3">
            {/* 移动端菜单按钮 */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
            >
              <FontAwesomeIcon icon={sidebarOpen ? faTimes : faBars} className="h-6 w-6" />
            </button>

            {/* 搜索框 */}
            <div className="flex-1 max-w-lg mx-4">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon={faSearch} className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="搜索..."
                />
              </div>
            </div>

            {/* 右侧操作区 */}
            <div className="flex items-center space-x-4">
              {/* 通知 */}
              <div className="relative">
                <button
                  onClick={toggleNotifications}
                  className="p-2 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full"
                >
                  <FontAwesomeIcon icon={faBell} className="h-6 w-6" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </button>

                {/* 通知下拉菜单 */}
                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                    <div className="p-4">
                      <h3 className="text-lg font-medium text-gray-900 mb-3">通知</h3>
                      <div className="space-y-3 max-h-64 overflow-y-auto">
                        {notifications.length > 0 ? (
                          notifications.map((notification) => (
                            <div
                              key={notification.id}
                              className={`p-3 rounded-lg cursor-pointer transition-colors ${
                                notification.unread ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'
                              }`}
                              onClick={() => markNotificationAsRead(notification.id)}
                            >
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-gray-900">
                                    {notification.title}
                                  </p>
                                  <p className="text-sm text-gray-600 mt-1">
                                    {notification.message}
                                  </p>
                                </div>
                                {notification.unread && (
                                  <div className="w-2 h-2 bg-blue-500 rounded-full ml-2 mt-1"></div>
                                )}
                              </div>
                              <p className="text-xs text-gray-400 mt-2">{notification.time}</p>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-gray-500 text-center py-4">暂无通知</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* 用户头像 */}
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <FontAwesomeIcon icon={faUser} className="text-blue-600 text-sm" />
              </div>
            </div>
          </div>
        </header>

        {/* 页面内容 */}
        <main className="p-6">
          {children}
        </main>
      </div>

      {/* 移动端遮罩 */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default AdminLayout;